import React, { useMemo } from 'react';
import './styles.scss';

export interface SegmentedProgressBarSegment {
  label: string;
  value: number;
  color: string;
  tooltip?: string;
}

export interface SegmentedProgressBarProps {
  segments: SegmentedProgressBarSegment[];
  total: number;
  showLegend?: boolean;
  className?: string;
}

export interface ProcessedSegment extends SegmentedProgressBarSegment {
  percentage: number;
  left: number;
}

export function calculateSegmentPercentages(segments: SegmentedProgressBarSegment[], total: number): ProcessedSegment[] {
  if (total <= 0) return [];

  let cumulative = 0;
  return segments.map(segment => {
    const percentage = Math.max(0, Math.min(100, (segment.value / total) * 100));
    const left = cumulative;
    cumulative += percentage;
    return { ...segment, percentage, left };
  });
}

const Segment = ({ segment }: { segment: ProcessedSegment }) => (
  <div
    className="segmented-progress-bar__segment"
    style={{
      left: `${segment.left}%`,
      width: `${segment.percentage}%`,
      backgroundColor: segment.color,
    }}
    title={segment.tooltip || segment.label}
  />
);

const LegendItem = ({ segment }: { segment: SegmentedProgressBarSegment }) => (
  <div className="segmented-progress-bar__legend-item">
    <div className="segmented-progress-bar__legend-dot" style={{ backgroundColor: segment.color }} />
    <span className="segmented-progress-bar__legend-value">
      {segment.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  </div>
);

function SegmentedProgressBar({ segments, total, showLegend = true, className = '' }: SegmentedProgressBarProps) {
  const processedSegments = useMemo(() => calculateSegmentPercentages(segments, total), [segments, total]);

  return (
    <div className={`segmented-progress-bar ${className}`}>
      <div className="segmented-progress-bar__container">
        {processedSegments.map((segment, index) => (
          <Segment key={index} segment={segment} />
        ))}
      </div>

      {showLegend && (
        <div className="segmented-progress-bar__legend">
          {segments.map((segment, index) => (
            <LegendItem key={index} segment={segment} />
          ))}
        </div>
      )}
    </div>
  );
}

export default SegmentedProgressBar;

